import { useIsMobile } from "@/hooks/use-mobile";
import { getBaseRoute } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetDependencyTree, useGetTaskList } from "@/services";
import { Position } from "@xyflow/react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const useTaskHierarchy = () => {
  const {id, action, planId, planAction} = useParams();
  const navigate = useNavigate();
  const { projectData, planData } = useSelector(
    (state: RootState) => state.currentData
  );
  const basePath = getBaseRoute(useLocation().pathname);
  const isMobile = useIsMobile();

  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [selectedTab, setSelectedTab] = useState<"predecessors" | "successors">("predecessors");
  
  const isPredecessors = selectedTab == "predecessors";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${basePath}/${routes.projectOverview}`,
        name: `Project Overview`,
      },
      {
        link: `/${basePath}/${routes.projectOverview}/${id}/${action}/${routes.projectPlan}`,
        name: `${projectData?.name} Project Details`
      },
      {
        link: `/${basePath}/${routes.projectOverview}/${id}/${action}/${routes.projectPlan}/${planId}/${planAction}`,
        name: `${planData?.name} Details`
      },
      {
        link: "",
        name: 'View Task Hierarchy'
      },
    ],
    [projectData, planData]
  );

  const {data: taskList, isLoading: isLoadingTaskList } = useGetTaskList({
    projectId: id as string,
    planId: planId as string,
    queryParams: {
      enabled: !!(id && planId)
    }
  })

  const onTaskChange = (taskId: string) => {
    const task = taskList?.data?.find((data: any) => data?.taskId?.toString() == taskId);
    setSelectedTask(task);
  }

  const {data: treeTaskList, isLoading: isTreeLoading, refetch: refetchTreeTaskList, isRefetching: isTreeRefetching } = useGetDependencyTree({
    projectId: id as string,
    taskId: selectedTask?.taskId as string,
    filter: {
      direction: selectedTab
    },
    queryParams: {
      enabled: !!(id && selectedTask?.taskId)
    }
  })
  
  useEffect(() => {
    refetchTreeTaskList();
  }, [selectedTab, selectedTask])

  const transformData = (apiResponse: any) => {
    const nodes: any = [];
    const edges: any = [];
  
    // Spacing constants
    const X_OFFSET = 200; // Distance between columns
    const Y_OFFSET = 100; // Distance between rows
  
    // Track the current Y position for each column (level) to prevent overlapping
    const levelCounter: any = {};

    const traverse = (item: any, parentId = null, level = 0) => {
      const currentId: any = `n${item?.taskId}`;

      if (levelCounter[level] === undefined) levelCounter[level] = 0;

      // --- COORDINATE CALCULATIONS ---
      let position = { x: 0, y: 0 };

      if (isMobile) {
        // MOBILE: Vertical Layout
        // If Predecessors: Root is Top (0), children go Down (positive Y)
        // If Successors: Root is Bottom (0), children go Up (negative Y)
        position = {
          x: levelCounter[level] * X_OFFSET,
          y: isPredecessors ? Y_OFFSET : -(Y_OFFSET)
        };
      } else {
        // DESKTOP: Horizontal Layout (Current Logic)
        position = {
          x: isPredecessors ? -(X_OFFSET) : X_OFFSET,
          y: levelCounter[level] * Y_OFFSET
        };
      }

      // --- HANDLE POSITIONS ---
      let sourcePos, targetPos;
      if (isMobile) {
        // For Top-to-Bottom: Source is Bottom, Target is Top
        // For Bottom-to-Top: Source is Top, Target is Bottom
        sourcePos = isPredecessors ? Position.Bottom : Position.Top;
        targetPos = isPredecessors ? Position.Top : Position.Bottom;
      } else {
        sourcePos = isPredecessors ? Position.Left : Position.Right;
        targetPos = isPredecessors ? Position.Right : Position.Left;
      }
  
      nodes.push({
        id: currentId,
        type: 'custom',
        parentId,
        position,
        data: { 
          label: item?.taskName || "",
          ...item,
        },
        hidden: !!parentId,
        sourcePosition: sourcePos,
        targetPosition: targetPos,
      });
  
      levelCounter[level] += 1;
  
      // 2. Create the Edge if there's a parent
      if (parentId) {
        edges.push({
          id: `e${parentId}-${currentId}`,
          source: parentId,
          target: currentId,
          hidden: !parentId,
          type: 'step', // 'step' creates the right-angle lines seen in your screenshot
        });
      }
  
      // 3. Recurse through children
      if (item?.children && item?.children?.length > 0) {
        item?.children?.forEach((child: any) => {
          traverse(child, currentId, level + 1);
        });
      }
    };
  
    // Start the recursion with the root data
    traverse(apiResponse);
  
    return { nodes, edges };
  };

  const getTreeData = useMemo(() => {
      return transformData(treeTaskList?.data);
  }, [treeTaskList])

  const onTabClick = (value: any) => {
    setSelectedTab(value);
  };

  const onBackClick = () => {
    navigate(`/${basePath}/${routes.projectOverview}/${id}/${action}/${routes.projectPlan}/${planId}/${planAction}`)
  }

  return {
    breadcrumbData,
    isLoadingTaskList,
    taskList: taskList?.data || [],
    selectedTask,
    onTaskChange,
    isTreeLoading,
    getTreeData,
    isTreeRefetching,
    selectedTab,
    onTabClick,
    onBackClick,
    isPredecessors
  }

}

export default useTaskHierarchy;